import React, { useEffect, useState } from 'react';
import { Field } from 'formik';
import { useIntl } from 'react-intl';

import { useCurrentTheme } from 'app/shared/theme';
import { Box, Grid, GridItem, Typography } from 'app/shared/components/atoms';
import { NavigationLink as Link } from 'app/shared/components/atoms';
import { Textfield } from 'app/shared/components/molecules';

interface AttendeeDetailFormSectionProps {
  index: number;
  initiallyDisabled: boolean;
  handleChange: any;
  values: any;
  errors: any;
  setFormDirty: any;
  status?: { success: boolean; error: string | undefined };
}

export const AttendeeDetailFormSection: React.FC<
  React.PropsWithChildren<AttendeeDetailFormSectionProps>
> = ({
  index,
  values,
  errors,
  initiallyDisabled,
  handleChange,
  setFormDirty,
  status,
}) => {
  const { colors } = useCurrentTheme();
  const { formatMessage } = useIntl();
  const [fieldsDisabled, setFieldsDisabled] = useState(initiallyDisabled);
  const [showEditButton, setShowEditButton] = useState(initiallyDisabled);

  function handleClickEditButton() {
    setFieldsDisabled(false);
    setShowEditButton(false);
    setFormDirty(true);
  }

  useEffect(() => {
    if (!status?.error && !!status?.success && initiallyDisabled) {
      setShowEditButton(true);
      setFormDirty(false);
      setFieldsDisabled(true);
    }
  }, [status, initiallyDisabled]);

  return (
    <Box flexDirection="column">
      <Box mb={4} alignItems="center">
        <Typography
          color={colors.primary.white.base}
          textTransform="uppercase"
          fontWeight="semibold"
          fontSize="fontSize300"
        >
          {`Attendee ${Number(index) + 1}`}
        </Typography>
        {showEditButton && (
          <Link
            ml={2}
            onClick={handleClickEditButton}
            variant="primary"
            fontWeight="semibold"
          >
            EDIT
          </Link>
        )}
      </Box>
      <Grid gap={6} numberOfColumns={1}>
        <GridItem mobile={1}>
          <Field
            as={Textfield}
            backgroundColor={colors.primary.white.base}
            label={formatMessage({
              id: 'event.attendeeDetailsForm.firstName',
            })}
            labelColor={colors.primary.white.base}
            name={`firstName-${index}`}
            value={values[`firstName-${index}`] || ''}
            isDisabled={fieldsDisabled}
            hasError={false}
            onChange={handleChange}
          />
        </GridItem>
        <GridItem mobile={1}>
          <Field
            as={Textfield}
            backgroundColor={colors.primary.white.base}
            label={formatMessage({
              id: 'event.attendeeDetailsForm.lastName',
            })}
            labelColor={colors.primary.white.base}
            name={`lastName-${index}`}
            value={values[`lastName-${index}`] || ''}
            isDisabled={fieldsDisabled}
            hasError={false}
            onChange={handleChange}
          />
        </GridItem>
        <GridItem mobile={1}>
          <Field
            as={Textfield}
            backgroundColor={colors.primary.white.base}
            label={formatMessage({
              id: 'event.attendeeDetailsForm.email',
            })}
            labelColor={colors.primary.white.base}
            name={`email-${index}`}
            value={values[`email-${index}`] || ''}
            isDisabled={fieldsDisabled}
            hasError={!!errors[`email-${index}`]}
            helperText={formatMessage({
              id: 'event.attendeeDetailsForm.helperText',
            })}
            onChange={handleChange}
          />
        </GridItem>
      </Grid>
    </Box>
  );
};
